import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Project, Product, KeywordWithIntent, VolumeSortOption, VOLUME_SORT_LABELS, DeduplicationStrategy, DeduplicationScope, DeduplicationState } from '../types';
import { useProjects } from '../contexts/ProjectContext';
import { 
  getIntentCategories, 
  getIntentColorForProject, 
  getIntentLabelForProject 
} from '../utils/intentCategories';
import { exportSelectionsToExcel } from '../utils/exportSelections';
import { exportGroupNotesToExcel } from '../utils/exportGroupNotes';
import AutoSelectModal, { AutoSelectOptions } from './AutoSelectModal';
import AutoSelectIntentModal, { AutoSelectIntentOptions } from './AutoSelectIntentModal';
import DeduplicationModal from './DeduplicationModal';
import { 
  analyzeProductDuplication, 
  analyzePortfolioDuplication,
  ProductDuplicationAnalysis,
  PortfolioDuplicationAnalysis
} from '../utils/duplicationAnalysis';
import { selectTopIntentsByOverlap, getKeywordsForIntents, isExcludedIntent } from '../utils/intentOverlapAnalysis';
import { applyDeduplication, restoreFromDeduplication } from '../utils/deduplication';
import KeywordPill from './KeywordPill';
import CopyKeywordPanel from './CopyKeywordPanel';
import { 
  copyKeywordToMultipleProducts, 
  removeCopiedKeyword
} from '../utils/keywordCopy';
import GroupNotesModal from './GroupNotesModal';

const ProjectEvaluation: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { getProject, updateProject } = useProjects();
  const [project, setProject] = useState<Project | null>(null);
  const [selectedKeywords, setSelectedKeywords] = useState<{[productId: string]: string[]}>({});
  const [customKeywords, setCustomKeywords] = useState<{[productId: string]: KeywordWithIntent[]}>({});
  const [customKeyword, setCustomKeyword] = useState('');
  const [customKeywordVolume, setCustomKeywordVolume] = useState<string>('0');
  const [expandedCustomKeywordProductId, setExpandedCustomKeywordProductId] = useState<string | null>(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [volumeSort, setVolumeSort] = useState<VolumeSortOption>('by-intent');
  const [showVolume, setShowVolume] = useState<boolean>(true);
  const [showIntentScore, setShowIntentScore] = useState<boolean>(true);
  const [showAutoSelectModal, setShowAutoSelectModal] = useState<boolean>(false);
  const [showAutoSelectIntentModal, setShowAutoSelectIntentModal] = useState<boolean>(false);
  const [showDeduplicationModal, setShowDeduplicationModal] = useState<boolean>(false);
  const [deduplicationState, setDeduplicationState] = useState<DeduplicationState | null>(null);
  const [preselectedProductId, setPreselectedProductId] = useState<string | undefined>(undefined);
  const [preselectedProductIdIntent, setPreselectedProductIdIntent] = useState<string | undefined>(undefined);
  const [excludedTerms, setExcludedTerms] = useState<string[]>([]);
  const [excludeTermInput, setExcludeTermInput] = useState<string>('');
  const [showExcludedKeywords, setShowExcludedKeywords] = useState<boolean>(false);
  const [selectedIntents, setSelectedIntents] = useState<string[]>([]);
  const [explicitlySelectedIntents, setExplicitlySelectedIntents] = useState<{[productId: string]: string[]}>({});
  const [selectedRetailers, setSelectedRetailers] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [tableSortColumn, setTableSortColumn] = useState<'product' | 'keyword' | 'intent' | 'volume'>('product');
  const [tableSortDirection, setTableSortDirection] = useState<'asc' | 'desc'>('asc');
  const [scrollToProductId, setScrollToProductId] = useState<string | null>(null);
  const [duplicationThreshold, setDuplicationThreshold] = useState<number>(0.4); // 40% threshold (editable)
  const [portfolioDuplication, setPortfolioDuplication] = useState<PortfolioDuplicationAnalysis | null>(null);
  const [productDuplications, setProductDuplications] = useState<Map<string, ProductDuplicationAnalysis>>(new Map());
  const [showDuplicationModal, setShowDuplicationModal] = useState<boolean>(false);
  const [duplicationModalType, setDuplicationModalType] = useState<'portfolio' | string | null>(null); // 'portfolio' or productId
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [keywordViewMode, setKeywordViewMode] = useState<'coverage' | 'general'>('general');
  const [portfolioCoverageExpanded, setPortfolioCoverageExpanded] = useState<boolean>(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState<boolean>(false);
  const [selectedProductForInfo, setSelectedProductForInfo] = useState<string | null>(null);
  const [showInfoPanel, setShowInfoPanel] = useState<boolean>(false);
  const [showIngredientsColumn, setShowIngredientsColumn] = useState<boolean>(true);
  const [showLegalClaimsColumn, setShowLegalClaimsColumn] = useState<boolean>(true);
  
  // Group Notes Feature State
  const [showGroupNotesModal, setShowGroupNotesModal] = useState<boolean>(false);
  const [selectedGroupForNotes, setSelectedGroupForNotes] = useState<{key: string; name: string} | null>(null);
  
  // Keyword Copy Feature State
  const [draggedKeyword, setDraggedKeyword] = useState<{keyword: KeywordWithIntent; productId: string; productName: string} | null>(null);
  const [dropTargetProductId, setDropTargetProductId] = useState<string | null>(null);
  const [showCopyPanel, setShowCopyPanel] = useState<boolean>(false);
  const [keywordToCopy, setKeywordToCopy] = useState<{keyword: KeywordWithIntent; sourceProductId: string; sourceName: string} | null>(null);
  const [toastMessage, setToastMessage] = useState<{message: string; type: 'success' | 'error' | 'warning'} | null>(null);

  useEffect(() => {
    if (projectId) {
      const foundProject = getProject(projectId);
      if (foundProject) {
        setProject(foundProject);
        // Initialize selected keywords for all products
        const initialKeywords: {[productId: string]: string[]} = {};
        foundProject.products.forEach(product => {
          initialKeywords[product.id] = product.selectedKeywords;
        });
        setSelectedKeywords(initialKeywords);
      } else {
        // Project was deleted, redirect to home
        navigate('/');
      }
    }
  }, [projectId, getProject, navigate]);

  // Recalculate duplication analysis when selectedKeywords or project changes
  useEffect(() => {
    if (!project) return;

    // Analyze per-product duplication
    const productAnalysis = new Map<string, ProductDuplicationAnalysis>();
    project.products.forEach(product => {
      const productSelectedKeywords = selectedKeywords[product.id] || [];
      const analysis = analyzeProductDuplication(
        product.id,
        product.name,
        productSelectedKeywords,
        duplicationThreshold
      );
      productAnalysis.set(product.id, analysis);
    });
    setProductDuplications(productAnalysis);

    // Analyze portfolio-wide duplication
    const portfolioData = project.products.map(product => ({
      id: product.id,
      name: product.name,
      selectedKeywords: selectedKeywords[product.id] || []
    }));
    const portfolioAnalysis = analyzePortfolioDuplication(portfolioData, duplicationThreshold);
    setPortfolioDuplication(portfolioAnalysis);
  }, [selectedKeywords, project, duplicationThreshold]);

  // Helper function to get total selected keywords count
  const getTotalSelectedKeywords = () => {
    return Object.values(selectedKeywords).reduce((sum, keywords) => sum + keywords.length, 0);
  };

  // Get available retailers from the dataset
  const getAvailableRetailers = (): string[] => {
    if (!project) return [];
    
    const retailerSet = new Set<string>();
    const retailers: ('amazon' | 'target' | 'walmart' | 'kroger' | 'instacart')[] = 
      ['amazon', 'target', 'walmart', 'kroger', 'instacart'];
    
    project.products.forEach(product => {
      product.keywords.forEach(keyword => {
        retailers.forEach(retailer => {
          if (keyword[retailer] === true) {
            const displayName = retailer.charAt(0).toUpperCase() + retailer.slice(1);
            retailerSet.add(displayName);
          }
        });
      });
    });
    
    return Array.from(retailerSet).sort();
  };

  const availableRetailers = getAvailableRetailers();

  // Helper function to get group key for a product based on current view type
  const getGroupKey = (product: Product): string => {
    if (!project) return product.id;
    
    const viewType = project.viewType;
    if (!viewType || viewType === 'product') {
      return product.id;
    } else if (viewType === 'brand') {
      return product.brand || 'Unknown Brand';
    } else {
      return product.subBrand || 'Unknown Sub-Brand';
    }
  };

  // Helper function to get display products (groups products by brand/sub-brand if needed)
  const getDisplayProducts = (): Product[] => {
    if (!project) return [];
    
    const viewType = project.viewType;
    
    // If viewing by product (or no viewType), return as-is
    if (!viewType || viewType === 'product') {
      return project.products;
    }
    
    // Group by brand or sub-brand
    const groups = new Map<string, Product[]>();
    project.products.forEach(product => {
      const key = viewType === 'brand' 
        ? (product.brand || 'Unknown Brand')
        : (product.subBrand || 'Unknown Sub-Brand');
      
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(product);
    });
    
    // Create virtual merged products
    return Array.from(groups.values()).map(productsInGroup => {
      const first = productsInGroup[0];
      
      // Merge all keywords from all products in the group
      // Do NOT deduplicate here - show all keywords including duplicates
      // This makes it clear when duplicates exist and gives the deduplication feature a clear purpose
      const allKeywords: KeywordWithIntent[] = [];
      productsInGroup.forEach(p => allKeywords.push(...p.keywords));
      
      return {
        ...first,
        id: first.id, // Use first product's ID for selection tracking
        name: viewType === 'brand' ? (first.brand || 'Unknown Brand') : (first.subBrand || 'Unknown Sub-Brand'),
        keywords: allKeywords,
        description: productsInGroup.length > 1 
          ? `${productsInGroup.length} products in this ${viewType}`
          : first.description
      };
    });
  };

  const handleKeywordToggle = (productId: string, keyword: string) => {
    setSelectedKeywords(prev => {
      const productKeywords = prev[productId] || [];
      if (productKeywords.includes(keyword)) {
        return {
          ...prev,
          [productId]: productKeywords.filter(k => k !== keyword)
        };
      } else {
        return {
          ...prev,
          [productId]: [...productKeywords, keyword]
        };
      }
    });
  };

  const handleAddCustomKeyword = (productId: string) => {
    if (customKeyword.trim()) {
      const keywordText = customKeyword.trim();
      const volume = parseInt(customKeywordVolume) || 0;
      const productKeywords = selectedKeywords[productId] || [];
      const existingCustomKeywords = customKeywords[productId] || [];
      
      // Check if keyword already exists (either as regular or custom)
      const product = project?.products.find(p => p.id === productId);
      const keywordExists = product?.keywords.some(k => k.text === keywordText) || 
                            existingCustomKeywords.some(k => k.text === keywordText);
      
      if (!keywordExists && !productKeywords.includes(keywordText)) {
        // All custom keywords belong to the same "custom" intent category
        const customIntent = 'custom';
        const color = 'bg-gray-100 text-gray-800 border-gray-200';
        
        // Add to custom keywords
        const newCustomKeyword: KeywordWithIntent = {
          text: keywordText,
          intent: customIntent,
          volume: volume
        };
        
        setCustomKeywords(prev => ({
          ...prev,
          [productId]: [...existingCustomKeywords, newCustomKeyword]
        }));
        
        // Also add to selected keywords
        setSelectedKeywords(prev => ({
          ...prev,
          [productId]: [...productKeywords, keywordText]
        }));
        
        // Update project's intent category config if needed (only once for all custom keywords)
        if (project) {
          const updatedIntentCategories = project.intentCategories || [];
          if (!updatedIntentCategories.includes(customIntent)) {
            // Add the "custom" intent category to project
            const updatedConfig = {
              ...project.intentCategoryConfig,
              [customIntent]: {
                label: 'Custom',
                color: color
              }
            };
            
            setProject({
              ...project,
              intentCategories: [...updatedIntentCategories, customIntent],
              intentCategoryConfig: updatedConfig
            });
          }
        }
        
        setCustomKeyword('');
        setCustomKeywordVolume('0');
        setExpandedCustomKeywordProductId(null);
      }
    }
  };

  const handleToggleCustomKeywordInput = (productId: string) => {
    if (expandedCustomKeywordProductId === productId) {
      // Collapse if already expanded
      setExpandedCustomKeywordProductId(null);
      setCustomKeyword('');
      setCustomKeywordVolume('0');
    } else {
      // Expand for this product
      setExpandedCustomKeywordProductId(productId);
      setCustomKeyword('');
      setCustomKeywordVolume('0');
    }
  };

  const handleDeleteCustomKeyword = (productId: string, keywordText: string) => {
    // Remove from custom keywords
    setCustomKeywords(prev => {
      const productCustomKeywords = prev[productId] || [];
      return {
        ...prev,
        [productId]: productCustomKeywords.filter(k => k.text !== keywordText)
      };
    });
    
    // Remove from selected keywords if it was selected
    setSelectedKeywords(prev => {
      const productKeywords = prev[productId] || [];
      return {
        ...prev,
        [productId]: productKeywords.filter(k => k !== keywordText)
      };
    });
  };

  const isCustomKeyword = (productId: string, keywordText: string): boolean => {
    const productCustomKeywords = customKeywords[productId] || [];
    return productCustomKeywords.some(k => k.text === keywordText);
  };

  // Clean selectedKeywords after deduplication to remove selections for keywords that no longer exist
  const cleanSelectedKeywordsAfterDeduplication = (
    currentSelectedKeywords: {[productId: string]: string[]},
    modifiedProducts: Product[]
  ): {[productId: string]: string[]} => {
    console.log('[DEBUG] Starting cleanSelectedKeywordsAfterDeduplication');
    console.log('[DEBUG] Current selections:', currentSelectedKeywords);
    
    // Create a map of productId -> Set of keywords that still exist in the product after deduplication
    const existingKeywordsByProduct = new Map<string, Set<string>>();
    
    modifiedProducts.forEach(product => {
      const keywordTexts = new Set(product.keywords.map(kw => kw.text));
      existingKeywordsByProduct.set(product.id, keywordTexts);
    });

    console.log('[DEBUG] Keywords that still exist after deduplication:');
    existingKeywordsByProduct.forEach((keywords, productId) => {
      const productName = modifiedProducts.find(p => p.id === productId)?.name || productId;
      console.log(`  Product: ${productName}`, Array.from(keywords));
    });

    // Create cleaned selectedKeywords, keeping only keywords that still exist in the product
    const cleanedSelectedKeywords: {[productId: string]: string[]} = {};
    
    Object.keys(currentSelectedKeywords).forEach(productId => {
      const productSelections = currentSelectedKeywords[productId] || [];
      const existingKeywords = existingKeywordsByProduct.get(productId);
      const productName = modifiedProducts.find(p => p.id === productId)?.name || productId;
      
      if (!existingKeywords) {
        // Product doesn't exist anymore (shouldn't happen), clear selections
        cleanedSelectedKeywords[productId] = [];
        console.log(`[DEBUG] Product "${productName}": Product not found, clearing all selections`);
      } else {
        // Keep only keywords that still exist in the product
        const beforeCount = productSelections.length;
        cleanedSelectedKeywords[productId] = productSelections.filter(
          keyword => existingKeywords.has(keyword)
        );
        const afterCount = cleanedSelectedKeywords[productId].length;
        const removed = productSelections.filter(keyword => !existingKeywords.has(keyword));
        
        console.log(`[DEBUG] Product "${productName}": Had ${beforeCount} selections, removed ${beforeCount - afterCount}, kept ${afterCount}`);
        if (removed.length > 0) {
          console.log(`[DEBUG]   Removed from selections (no longer exist):`, removed);
        }
        if (afterCount > 0) {
          console.log(`[DEBUG]   Preserved selections (still exist):`, cleanedSelectedKeywords[productId]);
        }
      }
    });
    
    console.log('[DEBUG] Cleaned selections:', cleanedSelectedKeywords);
    return cleanedSelectedKeywords;
  };

  // Get table data - consolidated list with one row per keyword per product
  const getTableData = () => {
    if (!project) return [];
    
    const tableRows: Array<{
      productId: string;
      productName: string;
      keyword: string;
      intents: string[];
      volume: number;
      isCustom: boolean;
      ingredients?: string;
      legalClaims?: string;
    }> = [];
    
    project.products.forEach(product => {
      const productSelectedKeywords = selectedKeywords[product.id] || [];
      const productCustomKeywords = customKeywords[product.id] || [];
      
      // Group selected keywords by keyword text to consolidate duplicates
      const keywordMap = new Map<string, {
        intents: Set<string>;
        volume: number;
        isCustom: boolean;
      }>();
      
      productSelectedKeywords.forEach(keywordText => {
        // Find all instances of this keyword (might appear in multiple intents)
        const allKeywords = [...product.keywords, ...productCustomKeywords];
        const keywordInstances = allKeywords.filter(k => k.text === keywordText);
        
        if (keywordInstances.length > 0) {
          const isCustom = productCustomKeywords.some(k => k.text === keywordText);
          
          // Get or create entry for this keyword
          if (!keywordMap.has(keywordText)) {
            keywordMap.set(keywordText, {
              intents: new Set(),
              volume: keywordInstances[0].volume || 0,
              isCustom
            });
          }
          
          const entry = keywordMap.get(keywordText)!;
          
          // Add all intents this keyword belongs to
          keywordInstances.forEach(kw => {
            entry.intents.add(kw.intent);
            // Use the highest volume if they differ
            if (kw.volume > entry.volume) {
              entry.volume = kw.volume;
            }
          });
        }
      });
      
      // Convert map to array of table rows
      keywordMap.forEach((data, keywordText) => {
        // Filter by search query if provided
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase().trim();
          if (!keywordText.toLowerCase().includes(query)) {
            return; // Skip this keyword if it doesn't match search
          }
        }
        
        // Filter by selected intents if any are selected
        const intentsArray = Array.from(data.intents);
        if (selectedIntents.length === 0 || intentsArray.some(intent => selectedIntents.includes(intent))) {
          tableRows.push({
            productId: product.id,
            productName: product.name,
            keyword: keywordText,
            intents: intentsArray,
            volume: data.volume,
            isCustom: data.isCustom,
            ingredients: product.ingredients,
            legalClaims: product.legalClaims
          });
        }
      });
    });
    
    return tableRows;
  };

  const handleTableSort = (column: 'product' | 'keyword' | 'intent' | 'volume') => {
    if (tableSortColumn === column) {
      // Toggle direction if same column
      setTableSortDirection(tableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to asc
      setTableSortColumn(column);
      setTableSortDirection('asc');
    }
  };

  const getSortedTableData = () => {
    const data = getTableData();
    
    return [...data].sort((a, b) => {
      let comparison = 0;
      
      switch (tableSortColumn) {
        case 'product':
          comparison = a.productName.localeCompare(b.productName);
          break;
        case 'keyword':
          comparison = a.keyword.localeCompare(b.keyword);
          break;
        case 'intent':
          // Sort by first intent or by number of intents
          const aFirstIntent = a.intents[0] || '';
          const bFirstIntent = b.intents[0] || '';
          comparison = aFirstIntent.localeCompare(bFirstIntent);
          break;
        case 'volume':
          comparison = a.volume - b.volume;
          break;
      }
      
      return tableSortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleNavigateToProduct = (productId: string) => {
    // Switch to grid view
    setViewMode('grid');
    // Set the product to scroll to
    setScrollToProductId(productId);
  };

  // Effect to scroll to product when view mode changes and scrollToProductId is set
  useEffect(() => {
    if (viewMode === 'grid' && scrollToProductId) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const element = document.getElementById(`product-${scrollToProductId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Highlight the product card briefly
          element.classList.add('ring-4', 'ring-accent-400', 'ring-opacity-50');
          setTimeout(() => {
            element.classList.remove('ring-4', 'ring-accent-400', 'ring-opacity-50');
          }, 2000);
        }
        // Clear the scroll target
        setScrollToProductId(null);
      }, 100);
    }
  }, [viewMode, scrollToProductId]);

  const handleSaveProject = () => {
    if (project && projectId) {
      // Update each product's selected keywords
      const updatedProducts = project.products.map(product => ({
        ...product,
        selectedKeywords: selectedKeywords[product.id] || []
      }));
      
      // Update the project with new product data and custom keywords/intent config
      updateProject(projectId, {
        products: updatedProducts,
        intentCategories: project.intentCategories,
        intentCategoryConfig: project.intentCategoryConfig,
        groupNotes: project.groupNotes, // Preserve group notes
        updatedAt: new Date()
      });
      
      // Navigate to home screen
      navigate('/');
    }
  };

  // Group Notes Handlers
  const handleOpenNotesModal = (product: Product) => {
    const groupKey = getGroupKey(product);
    setSelectedGroupForNotes({ key: groupKey, name: product.name });
    setShowGroupNotesModal(true);
  };

  const handleCloseNotesModal = () => {
    setShowGroupNotesModal(false);
    setSelectedGroupForNotes(null);
  };

  const handleSaveGroupNote = (note: string) => {
    if (!project || !selectedGroupForNotes) return;
    
    const updatedGroupNotes = {
      ...(project.groupNotes || {}),
      [selectedGroupForNotes.key]: note.trim()
    };
    
    // Remove empty notes
    if (!note.trim()) {
      delete updatedGroupNotes[selectedGroupForNotes.key];
    }
    
    // Preserve current keyword selections by syncing local state to products
    const updatedProducts = project.products.map(product => ({
      ...product,
      selectedKeywords: selectedKeywords[product.id] || []
    }));
    
    const updatedProject = {
      ...project,
      products: updatedProducts,
      groupNotes: Object.keys(updatedGroupNotes).length > 0 ? updatedGroupNotes : undefined,
      updatedAt: new Date()
    };
    
    setProject(updatedProject);
    updateProject(project.id, updatedProject);
  };

  // Get current note for a group
  const getGroupNote = (product: Product): string => {
    if (!project || !project.groupNotes) return '';
    const groupKey = getGroupKey(product);
    return project.groupNotes[groupKey] || '';
  };

  const executeAutoSelect = (options: AutoSelectOptions) => {
    if (!project) return;

    const { productIds, intentCategories, strategy, topN, intentTopN } = options;

    // Process each selected product
    productIds.forEach(productId => {
      const product = project.products.find(p => p.id === productId);
      if (!product) return;

      // Filter keywords by selected intent categories
      let filteredKeywords = product.keywords.filter(keyword =>
        intentCategories.includes(keyword.intent)
      );

      // Filter out keywords containing excluded terms (case-insensitive)
      if (excludedTerms.length > 0) {
        filteredKeywords = filteredKeywords.filter(keyword => {
          const keywordLower = keyword.text.toLowerCase();
          return !excludedTerms.some(term => keywordLower.includes(term.toLowerCase()));
        });
      }

      // Group filtered keywords by intent category
      const keywordsByIntent: { [intent: string]: KeywordWithIntent[] } = {};
      filteredKeywords.forEach(keyword => {
        if (!keywordsByIntent[keyword.intent]) {
          keywordsByIntent[keyword.intent] = [];
        }
        keywordsByIntent[keyword.intent].push(keyword);
      });

      // Select keywords based on strategy
      const autoSelectedKeywords: string[] = [];
      Object.entries(keywordsByIntent).forEach(([intent, keywords]) => {
        // Sort by volume (highest first)
        const sortedKeywords = [...keywords].sort((a, b) => (b.volume || 0) - (a.volume || 0));
        
        if (strategy === 'top-volume-only') {
          // Select top 1 keyword
          if (sortedKeywords.length > 0) {
            autoSelectedKeywords.push(sortedKeywords[0].text);
          }
        } else if (strategy === 'top-n-per-intent' && topN) {
          // Determine how many keywords to select for this intent
          // Use per-intent value if available, otherwise use global topN
          const intentN = intentTopN?.[intent] ?? topN;
          
          // Select top N keywords for this intent
          const keywordsToSelect = sortedKeywords.slice(0, intentN);
          keywordsToSelect.forEach(keyword => {
            autoSelectedKeywords.push(keyword.text);
          });
        }
      });

      // Update selected keywords for this product (replacing existing)
      setSelectedKeywords(prev => ({
        ...prev,
        [productId]: autoSelectedKeywords
      }));
    });
  };

  const handleAutoSelect = (productId: string) => {
    setPreselectedProductId(productId);
    setShowAutoSelectModal(true);
  };

  const handleAutoSelectAll = () => {
    setPreselectedProductId(undefined);
    setShowAutoSelectModal(true);
  };

  const handleAutoSelectIntentProduct = (productId: string) => {
    setPreselectedProductIdIntent(productId);
    setShowAutoSelectIntentModal(true);
  };

  const handleAutoSelectModalConfirm = (options: AutoSelectOptions) => {
    executeAutoSelect(options);
  };

  const handleAutoSelectModalClose = () => {
    setShowAutoSelectModal(false);
    setPreselectedProductId(undefined);
  };

  const handleAutoSelectIntentAll = () => {
    setShowAutoSelectIntentModal(true);
  };

  const handleAutoSelectIntentModalConfirm = (options: AutoSelectIntentOptions) => {
    executeAutoSelectIntent(options);
  };

  const handleAutoSelectIntentModalClose = () => {
    setShowAutoSelectIntentModal(false);
    setPreselectedProductIdIntent(undefined);
  };

  const executeAutoSelectIntent = (options: AutoSelectIntentOptions) => {
    if (!project) return;

    const newExplicitlySelectedIntents: {[productId: string]: string[]} = {};

    // For each selected product, select top N intents by overlap
    options.productIds.forEach(productId => {
      const product = project.products.find(p => p.id === productId);
      if (!product) return;

      // Calculate which intents have the most overlap/coverage
      const topIntents = selectTopIntentsByOverlap(product, options.intentsPerProduct);
      
      // Get all keywords for the selected intents
      const keywordsToSelect = getKeywordsForIntents(product, topIntents);

      // Update selected keywords for this product
      setSelectedKeywords(prev => ({
        ...prev,
        [productId]: keywordsToSelect
      }));

      // Track that these intents were explicitly selected
      newExplicitlySelectedIntents[productId] = topIntents;
    });

    // Update explicitly selected intents
    setExplicitlySelectedIntents(prev => ({
      ...prev,
      ...newExplicitlySelectedIntents
    }));
  };

  const handleDeselectAll = () => {
    if (project) {
      // Clear all selections for all products
      const clearedKeywords: {[productId: string]: string[]} = {};
      project.products.forEach(product => {
        clearedKeywords[product.id] = [];
      });
      setSelectedKeywords(clearedKeywords);
      setExplicitlySelectedIntents({});
    }
  };

  const handleDeselectProduct = (productId: string) => {
    setSelectedKeywords(prev => ({
      ...prev,
      [productId]: []
    }));
    setExplicitlySelectedIntents(prev => ({
      ...prev,
      [productId]: []
    }));
  };

  const handleAddExcludedTerm = () => {
    const term = excludeTermInput.trim().toLowerCase();
    if (term && !excludedTerms.includes(term)) {
      setExcludedTerms(prev => [...prev, term]);
      setExcludeTermInput('');
    }
  };

  const handleRemoveExcludedTerm = (termToRemove: string) => {
    setExcludedTerms(prev => prev.filter(term => term !== termToRemove));
  };

  const handleClearAllExcludedTerms = () => {
    setExcludedTerms([]);
    setExcludeTermInput('');
  };

  // ============================================================================
  // KEYWORD COPY HANDLERS
  // ============================================================================

  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const handleKeywordDragStart = (e: React.DragEvent, keyword: KeywordWithIntent, productId: string) => {
    const product = project?.products.find(p => p.id === productId);
    const productName = product?.name || 'Unknown';
    
    setDraggedKeyword({ keyword, productId, productName });
    
    // Set drag data for compatibility
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', keyword.text);
  };

  const handleKeywordDragEnd = () => {
    setDraggedKeyword(null);
    setDropTargetProductId(null);
  };

  const handleProductDragOver = (e: React.DragEvent, targetProductId: string) => {
    if (!draggedKeyword) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTargetProductId(targetProductId);
  };

  const handleProductDragLeave = () => {
    setDropTargetProductId(null);
  };

  const handleProductDrop = (e: React.DragEvent, targetProductId: string) => {
    e.preventDefault();
    
    if (!draggedKeyword || !project) {
      setDropTargetProductId(null);
      setDraggedKeyword(null);
      return;
    }

    // Don't copy to same product
    if (draggedKeyword.productId === targetProductId) {
      showToast('Cannot copy keyword to the same product', 'warning');
      setDropTargetProductId(null);
      setDraggedKeyword(null);
      return;
    }

    const targetProduct = project.products.find(p => p.id === targetProductId);
    if (!targetProduct) {
      setDropTargetProductId(null);
      setDraggedKeyword(null);
      return;
    }

    // Execute the copy
    executeCopy(draggedKeyword.keyword, draggedKeyword.productId, draggedKeyword.productName, [targetProductId]);
    
    setDropTargetProductId(null);
    setDraggedKeyword(null);
  };

  const handleKeywordContextMenu = (e: React.MouseEvent, keyword: KeywordWithIntent, productId: string) => {
    e.preventDefault();
    
    const product = project?.products.find(p => p.id === productId);
    const productName = product?.name || 'Unknown';
    
    setKeywordToCopy({
      keyword,
      sourceProductId: productId,
      sourceName: productName
    });
    setShowCopyPanel(true);
  };

  const executeCopy = (
    keyword: KeywordWithIntent,
    sourceProductId: string,
    sourceName: string,
    targetProductIds: string[]
  ) => {
    if (!project) return;

    const viewType = project.viewType || 'product';
    const sourceProduct = project.products.find(p => p.id === sourceProductId);
    const sourceProductIsSelected = sourceProduct ? 
      (selectedKeywords[sourceProductId] || []).includes(keyword.text) : false;

    const targetProducts = project.products.filter(p => targetProductIds.includes(p.id));

    const { updatedProducts, skippedProducts } = copyKeywordToMultipleProducts(
      keyword,
      sourceProductId,
      sourceName,
      viewType,
      targetProducts,
      sourceProductIsSelected
    );

    if (updatedProducts.length === 0 && skippedProducts.length > 0) {
      showToast(`Keyword already exists in ${skippedProducts.length === 1 ? 'target' : 'all targets'}`, 'warning');
      return;
    }

    // Update project with modified products, preserving current selections for all products
    const updatedAllProducts = project.products.map(p => {
      const updated = updatedProducts.find(up => up.id === p.id);
      if (updated) {
        // For target products: preserve current selections from local state,
        // then add the newly copied keyword if it was selected in source
        const currentSelections = selectedKeywords[p.id] || [];
        const newKeywordWasAdded = sourceProductIsSelected && !currentSelections.includes(keyword.text);
        const mergedSelections = newKeywordWasAdded 
          ? [...currentSelections, keyword.text]
          : currentSelections;
        return {
          ...updated,
          selectedKeywords: mergedSelections
        };
      }
      // Preserve current selections for non-updated products
      return {
        ...p,
        selectedKeywords: selectedKeywords[p.id] || []
      };
    });

    const updatedProject = { ...project, products: updatedAllProducts };
    setProject(updatedProject);
    updateProject(project.id, updatedProject);

    // Update selectedKeywords state to match what we saved to project
    const newSelectedKeywords = { ...selectedKeywords };
    updatedAllProducts.forEach(p => {
      newSelectedKeywords[p.id] = p.selectedKeywords;
    });
    setSelectedKeywords(newSelectedKeywords);

    // Show success message
    if (updatedProducts.length > 0) {
      const successMsg = updatedProducts.length === 1
        ? `Copied "${keyword.text}" to ${updatedProducts[0].name}`
        : `Copied "${keyword.text}" to ${updatedProducts.length} products`;
      
      const warningMsg = skippedProducts.length > 0
        ? ` (skipped ${skippedProducts.length} duplicate${skippedProducts.length > 1 ? 's' : ''})`
        : '';
      
      showToast(successMsg + warningMsg, 'success');
    }
  };

  const handleCopyKeywordConfirm = (keyword: KeywordWithIntent, targetProductIds: string[]) => {
    if (!keywordToCopy) return;
    
    executeCopy(
      keyword,
      keywordToCopy.sourceProductId,
      keywordToCopy.sourceName,
      targetProductIds
    );
  };

  const handleRemoveCopiedKeyword = (productId: string, keywordText: string) => {
    if (!project) return;

    const product = project.products.find(p => p.id === productId);
    if (!product) return;

    try {
      const updatedProduct = removeCopiedKeyword(keywordText, product);
      
      // Preserve current selections for all products when updating
      const updatedAllProducts = project.products.map(p => {
        if (p.id === productId) {
          // For the product being modified: use current local selections,
          // but remove the keyword that was deleted
          const currentSelections = selectedKeywords[p.id] || [];
          const selectionsWithoutRemoved = currentSelections.filter(k => k !== keywordText);
          return {
            ...updatedProduct,
            selectedKeywords: selectionsWithoutRemoved
          };
        }
        // Preserve current selections for non-updated products
        return {
          ...p,
          selectedKeywords: selectedKeywords[p.id] || []
        };
      });

      const updatedProject = { ...project, products: updatedAllProducts };
      setProject(updatedProject);
      updateProject(project.id, updatedProject);

      // Update selectedKeywords state
      const updatedSelections = (selectedKeywords[productId] || []).filter(k => k !== keywordText);
      setSelectedKeywords(prev => ({
        ...prev,
        [productId]: updatedSelections
      }));

      showToast(`Removed copy of "${keywordText}" from ${product.name}`, 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to remove copy', 'error');
    }
  };

  const handleApplyDeduplication = (strategy: DeduplicationStrategy, scope: DeduplicationScope) => {
    if (!project) return;

    const { modifiedProducts, deduplicationState: newDeduplicationState } = applyDeduplication(
      project.products,
      project.viewType || 'product',
      scope,
      strategy
    );

    // Clean selectedKeywords to remove selections for keywords that no longer exist in products
    // If a keyword had duplicates and at least one instance was kept, it stays selected
    const cleanedSelectedKeywords = cleanSelectedKeywordsAfterDeduplication(
      selectedKeywords,
      modifiedProducts
    );

    // Count how many selections were removed
    const originalSelectionCount = Object.values(selectedKeywords).reduce((sum, keywords) => sum + keywords.length, 0);
    const cleanedSelectionCount = Object.values(cleanedSelectedKeywords).reduce((sum, keywords) => sum + keywords.length, 0);
    const selectionsRemoved = originalSelectionCount - cleanedSelectionCount;

    // Update product.selectedKeywords fields to match the cleaned selections
    // This ensures that when the component re-renders and re-initializes from project data,
    // the cleaned selections are preserved
    const productsWithCleanedSelections = modifiedProducts.map(product => ({
      ...product,
      selectedKeywords: cleanedSelectedKeywords[product.id] || []
    }));

    // Update states
    setSelectedKeywords(cleanedSelectedKeywords);
    const updatedProject = { ...project, products: productsWithCleanedSelections };
    setProject(updatedProject);
    updateProject(project.id, updatedProject);
    setDeduplicationState(newDeduplicationState);

    // Log results
    console.log(`[Deduplication] Applied with strategy="${strategy}", scope="${scope}"`);
    console.log(`[Deduplication] ${newDeduplicationState.removedCount} keyword instances removed`);
    console.log(`[Deduplication] ${selectionsRemoved} selections automatically cleared`);

    setShowDeduplicationModal(false);
  };

  const handleRestoreDeduplication = () => {
    if (!project || !deduplicationState) return;

    const restoredProducts = restoreFromDeduplication(project.products, deduplicationState);
    const updatedProject = { ...project, products: restoredProducts };
    setProject(updatedProject);
    updateProject(project.id, updatedProject);
    setDeduplicationState(null);
  };

  const handleIntentToggle = (intent: string) => {
    setSelectedIntents(prev => {
      if (prev.includes(intent)) {
        return prev.filter(i => i !== intent);
      } else {
        return [...prev, intent];
      }
    });
  };

  const handleClearAllIntents = () => {
    setSelectedIntents([]);
  };

  const handleSelectAllIntents = () => {
    if (project) {
      const allIntents = getIntentCategories(project);
      setSelectedIntents(allIntents);
    }
  };

  // Get count of keywords excluded by a specific term
  const getExcludedKeywordCountForTerm = (term: string): number => {
    if (!project) return 0;
    
    const seenKeywords = new Set<string>();
    const termLower = term.toLowerCase();
    
    project.products.forEach(product => {
      const productCustomKeywords = customKeywords[product.id] || [];
      const allKeywords = [...product.keywords, ...productCustomKeywords];
      
      allKeywords.forEach(keyword => {
        const keywordLower = keyword.text.toLowerCase();
        if (keywordLower.includes(termLower) && !seenKeywords.has(keyword.text)) {
          seenKeywords.add(keyword.text);
        }
      });
    });
    
    return seenKeywords.size;
  };

  // Get all excluded keywords across all products
  const getAllExcludedKeywords = (): KeywordWithIntent[] => {
    if (excludedTerms.length === 0 || !project) return [];
    
    const allExcluded: KeywordWithIntent[] = [];
    const seenKeywords = new Set<string>();
    
    project.products.forEach(product => {
      const productCustomKeywords = customKeywords[product.id] || [];
      const allKeywords = [...product.keywords, ...productCustomKeywords];
      
      allKeywords.forEach(keyword => {
        const keywordLower = keyword.text.toLowerCase();
        const matchesExcluded = excludedTerms.some(term => keywordLower.includes(term.toLowerCase()));
        
        if (matchesExcluded && !seenKeywords.has(keyword.text)) {
          seenKeywords.add(keyword.text);
          allExcluded.push(keyword);
        }
      });
    });
    
    // Apply same sorting as filtered keywords
    if (volumeSort === 'by-intent') {
      const keywordsByIntent: { [intent: string]: KeywordWithIntent[] } = {};
      allExcluded.forEach(keyword => {
        if (!keywordsByIntent[keyword.intent]) {
          keywordsByIntent[keyword.intent] = [];
        }
        keywordsByIntent[keyword.intent].push(keyword);
      });
      
      Object.keys(keywordsByIntent).forEach(intent => {
        keywordsByIntent[intent].sort((a, b) => b.volume - a.volume);
      });
      
      const intentOrder = project ? getIntentCategories(project) : [];
      const sortedIntents = Object.keys(keywordsByIntent).sort((a, b) => {
        const indexA = intentOrder.indexOf(a);
        const indexB = intentOrder.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
      });
      
      return sortedIntents.flatMap(intent => keywordsByIntent[intent]);
    } else {
      return [...allExcluded].sort((a, b) => {
        switch (volumeSort) {
          case 'volume-high':
            return b.volume - a.volume;
          case 'volume-low':
            return a.volume - b.volume;
          case 'alphabetical':
            return a.text.localeCompare(b.text);
          default:
            return 0;
        }
      });
    }
  };

  // Get excluded keywords for a product (keywords that match excluded terms)
  const getExcludedKeywords = (product: Product): KeywordWithIntent[] => {
    if (excludedTerms.length === 0) return [];
    
    // Merge regular keywords with custom keywords for this product
    const productCustomKeywords = customKeywords[product.id] || [];
    const allKeywords = [...product.keywords, ...productCustomKeywords];
    
    // Filter to only include keywords that contain excluded terms
    const excluded = allKeywords.filter(keyword => {
      const keywordLower = keyword.text.toLowerCase();
      return excludedTerms.some(term => keywordLower.includes(term.toLowerCase()));
    });
    
    // Apply same sorting as filtered keywords
    if (volumeSort === 'by-intent') {
      const keywordsByIntent: { [intent: string]: KeywordWithIntent[] } = {};
      excluded.forEach(keyword => {
        if (!keywordsByIntent[keyword.intent]) {
          keywordsByIntent[keyword.intent] = [];
        }
        keywordsByIntent[keyword.intent].push(keyword);
      });
      
      Object.keys(keywordsByIntent).forEach(intent => {
        keywordsByIntent[intent].sort((a, b) => b.volume - a.volume);
      });
      
      const intentOrder = project ? getIntentCategories(project) : [];
      const sortedIntents = Object.keys(keywordsByIntent).sort((a, b) => {
        const indexA = intentOrder.indexOf(a);
        const indexB = intentOrder.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
      });
      
      return sortedIntents.flatMap(intent => keywordsByIntent[intent]);
    } else {
      return [...excluded].sort((a, b) => {
        switch (volumeSort) {
          case 'volume-high':
            return b.volume - a.volume;
          case 'volume-low':
            return a.volume - b.volume;
          case 'alphabetical':
            return a.text.localeCompare(b.text);
          default:
            return 0;
        }
      });
    }
  };

  // Get intent category coverage for a product
  const getIntentCoverage = (product: Product) => {
    // Get all unique intent categories in this product's keywords
    const productCustomKeywords = customKeywords[product.id] || [];
    const allKeywords = [...product.keywords, ...productCustomKeywords];
    const uniqueIntents = new Set<string>();
    
    allKeywords.forEach(keyword => {
      uniqueIntents.add(keyword.intent);
    });

    // For each intent, determine if it's explicitly selected
    const intentCoverage: Array<{
      intent: string;
      label: string;
      color: string;
      isFulfilled: boolean;
      selectedCount: number;
      totalCount: number;
    }> = [];

    const productSelectedKeywords = selectedKeywords[product.id] || [];
    const productExplicitlySelectedIntents = explicitlySelectedIntents[product.id] || [];

    uniqueIntents.forEach(intent => {
      // Get all keywords in this product with this intent
      const keywordsWithIntent = allKeywords.filter(k => k.intent === intent);
      const totalCount = keywordsWithIntent.length;
      
      // Get selected keywords with this intent
      const selectedWithIntent = keywordsWithIntent.filter(k => 
        productSelectedKeywords.includes(k.text)
      );
      const selectedCount = selectedWithIntent.length;

      intentCoverage.push({
        intent,
        label: getIntentLabelForProject(project, intent),
        color: getIntentColorForProject(project, intent),
        isFulfilled: productExplicitlySelectedIntents.includes(intent),
        selectedCount,
        totalCount
      });
    });

    // Sort by intent order in project, with excluded intents (none, other, extra) at the bottom
    const intentOrder = project ? getIntentCategories(project) : [];
    intentCoverage.sort((a, b) => {
      const aIsExcluded = isExcludedIntent(a.intent);
      const bIsExcluded = isExcludedIntent(b.intent);
      
      // Always put excluded intents at the bottom
      if (aIsExcluded && !bIsExcluded) return 1;
      if (!aIsExcluded && bIsExcluded) return -1;
      
      // If both are excluded, sort alphabetically
      if (aIsExcluded && bIsExcluded) return a.label.localeCompare(b.label);
      
      // Normal sorting for non-excluded intents
      const indexA = intentOrder.indexOf(a.intent);
      const indexB = intentOrder.indexOf(b.intent);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.label.localeCompare(b.label);
    });

    return intentCoverage;
  };

  // Get portfolio-level intent coverage (across all products)
  const getPortfolioIntentCoverage = () => {
    if (!project) return [];

    // Track which products have fulfilled each intent category
    const intentStats = new Map<string, {
      productsWithIntent: Set<string>;
      productsFulfilled: Set<string>;
    }>();

    project.products.forEach(product => {
      const coverage = getIntentCoverage(product);
      
      coverage.forEach(({ intent, isFulfilled }) => {
        if (!intentStats.has(intent)) {
          intentStats.set(intent, {
            productsWithIntent: new Set(),
            productsFulfilled: new Set()
          });
        }
        
        const stats = intentStats.get(intent)!;
        stats.productsWithIntent.add(product.id);
        
        if (isFulfilled) {
          stats.productsFulfilled.add(product.id);
        }
      });
    });

    // Convert to array format
    const portfolioCoverage = Array.from(intentStats.entries()).map(([intent, stats]) => ({
      intent,
      label: getIntentLabelForProject(project, intent),
      color: getIntentColorForProject(project, intent),
      productsFulfilledCount: stats.productsFulfilled.size,
      productsWithIntentCount: stats.productsWithIntent.size,
      isFulfilled: stats.productsFulfilled.size === stats.productsWithIntent.size,
      percentage: (stats.productsFulfilled.size / stats.productsWithIntent.size) * 100
    }));

    // Sort by intent order in project
    const intentOrder = project ? getIntentCategories(project) : [];
    portfolioCoverage.sort((a, b) => {
      const indexA = intentOrder.indexOf(a.intent);
      const indexB = intentOrder.indexOf(b.intent);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.label.localeCompare(b.label);
    });

    return portfolioCoverage;
  };

  // Filter and sort keywords based on selection state and volume
  const getFilteredKeywords = (product: Product) => {
    // Merge regular keywords with custom keywords for this product
    const productCustomKeywords = customKeywords[product.id] || [];
    let keywords = [...product.keywords, ...productCustomKeywords];
    
    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      keywords = keywords.filter(keyword => {
        return keyword.text.toLowerCase().includes(query);
      });
    }
    
    // Filter out keywords containing excluded terms (case-insensitive)
    if (excludedTerms.length > 0) {
      keywords = keywords.filter(keyword => {
        const keywordLower = keyword.text.toLowerCase();
        return !excludedTerms.some(term => keywordLower.includes(term.toLowerCase()));
      });
    }
    
    // Filter by intent categories if any are selected
    if (selectedIntents.length > 0) {
      keywords = keywords.filter(keyword => selectedIntents.includes(keyword.intent));
    }
    
    // Filter by retailer availability
    if (selectedRetailers.length > 0) {
      keywords = keywords.filter(keyword => {
        const hasRetailerData = ['amazon', 'target', 'walmart', 'kroger', 'instacart'].some(
          r => keyword[r as keyof typeof keyword] !== undefined
        );
        if (!hasRetailerData) return true;
        return selectedRetailers.some(retailer => {
          const retailerKey = retailer.toLowerCase() as 'amazon' | 'target' | 'walmart' | 'kroger' | 'instacart';
          return keyword[retailerKey] === true;
        });
      });
    }
    
    // Filter by selection state
    if (showSelectedOnly) {
      const productKeywords = selectedKeywords[product.id] || [];
      keywords = keywords.filter(keyword => productKeywords.includes(keyword.text));
    }
    
    // Sort by volume or intent
    if (volumeSort === 'by-intent') {
        // Group by intent, then sort by volume high to low within each group
        // First, group keywords by intent
        const keywordsByIntent: { [intent: string]: KeywordWithIntent[] } = {};
        keywords.forEach(keyword => {
          if (!keywordsByIntent[keyword.intent]) {
            keywordsByIntent[keyword.intent] = [];
          }
          keywordsByIntent[keyword.intent].push(keyword);
        });
        
        // Sort keywords within each intent group by volume (high to low)
        Object.keys(keywordsByIntent).forEach(intent => {
          keywordsByIntent[intent].sort((a, b) => b.volume - a.volume);
        });
        
        // Get intent categories in their display order (from project)
        const intentOrder = project ? getIntentCategories(project) : [];
        
        // Sort intent groups by their order in the project's intent categories
        const sortedIntents = Object.keys(keywordsByIntent).sort((a, b) => {
          const indexA = intentOrder.indexOf(a);
          const indexB = intentOrder.indexOf(b);
          
          // If both intents are in the order list, sort by their position
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          // If only A is in the list, it comes first
          if (indexA !== -1) return -1;
          // If only B is in the list, it comes first
          if (indexB !== -1) return 1;
          // If neither is in the list, sort alphabetically
          return a.localeCompare(b);
        });
        
        // Flatten back into a single array, maintaining intent grouping
        keywords = sortedIntents.flatMap(intent => keywordsByIntent[intent]);
    } else {
      // Other sorting options
      keywords = [...keywords].sort((a, b) => {
        switch (volumeSort) {
          case 'volume-high':
            return b.volume - a.volume;
          case 'volume-low':
            return a.volume - b.volume;
          case 'alphabetical':
            return a.text.localeCompare(b.text);
          default:
            return 0;
        }
      });
    }
    
    return keywords;
  };

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-primary-900 mb-4">Project not found</h2>
        <Link to="/" className="btn-primary">Back to Projects</Link>
      </div>
    );
  }

  // Calculate all excluded keywords for display in Global Filter
  const allExcludedKeywords = showExcludedKeywords && excludedTerms.length > 0 
    ? getAllExcludedKeywords() 
    : [];

  return (
    <div className="flex">
      {/* Main Content */}
      <div className={`flex-1 space-y-6 transition-all duration-300 ${showInfoPanel ? 'mr-96' : 'mr-0'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/" className="text-gray-600 hover:text-accent-500">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-3xl font-bold text-primary-900">{project.name}</h2>
              {/* View Type Indicator */}
              {project.viewType && (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
                  project.viewType === 'product' 
                    ? 'bg-blue-100 text-blue-800 border border-blue-300'
                    : project.viewType === 'brand'
                    ? 'bg-purple-100 text-purple-800 border border-purple-300'
                    : 'bg-green-100 text-green-800 border border-green-300'
                }`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Viewing by {
                    project.viewType === 'product' 
                      ? 'Product Name'
                      : project.viewType === 'brand'
                      ? 'Brand'
                      : 'Sub-Brand'
                  }
                </span>
              )}
            </div>
            <p className="text-gray-600">{project.brand.name} • {project.products.length} products</p>
          </div>
        </div>
        {/* Download Original File Button */}
        {project.originalFile && (
          <button
            onClick={() => {
              // Convert base64 to blob and download
              const byteCharacters = atob(project.originalFile!.data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
              });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = project.originalFile!.name;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            title={`Download original file: ${project.originalFile.name}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="text-left">
              <div className="text-sm font-medium">View Input File</div>
              <div className="text-xs text-gray-500">{project.originalFile.name}</div>
            </div>
          </button>
        )}
      </div>

      {/* Global Filter */}
      <div className="card">
        {/* Search Bar and Primary Actions Row */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            
            {/* Primary Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleAutoSelectAll}
                className="px-4 py-2 bg-accent-500 text-white rounded-lg text-sm font-medium hover:bg-accent-600 transition-colors flex items-center space-x-2 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Auto Keyword</span>
              </button>
              <button
                onClick={handleAutoSelectIntentAll}
                className="px-4 py-2 bg-accent-500 text-white rounded-lg text-sm font-medium hover:bg-accent-600 transition-colors flex items-center space-x-2 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <span>Auto Intent</span>
              </button>
              <button
                onClick={() => setShowDeduplicationModal(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
                title="Remove duplicate keywords"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                </svg>
                <span>Duplicates</span>
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Clear All</span>
              </button>
            </div>
          </div>
          
          {searchQuery && (
            <p className="text-xs text-gray-500 mt-2">
              Filtering keywords matching "{searchQuery}"
            </p>
          )}
        </div>

        {/* Controls Row */}
        <div className="flex items-center justify-between gap-4">
          {/* Display Controls - Left */}
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-50 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-2 py-1.5 rounded text-sm font-medium transition-colors flex items-center ${
                  viewMode === 'grid'
                    ? 'bg-white text-primary-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid View"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-2 py-1.5 rounded text-sm font-medium transition-colors flex items-center ${
                  viewMode === 'table'
                    ? 'bg-white text-primary-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Table View"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>

            {viewMode === 'grid' && (
              <div className="flex items-center space-x-2 pl-3 border-l border-gray-300">
                <label className="text-xs font-medium text-gray-600">Sort by:</label>
                <select
                  value={volumeSort}
                  onChange={(e) => setVolumeSort(e.target.value as VolumeSortOption)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 appearance-none"
                  style={{ 
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2rem'
                  }}
                >
                  {Object.entries(VOLUME_SORT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Filter & Display Toggles - Right */}
          <div className="flex items-center gap-2">
            {viewMode === 'grid' && (
              <>
                <div className="flex items-center bg-gray-50 rounded-lg p-1">
                  <button
                    onClick={() => setKeywordViewMode('general')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      keywordViewMode === 'general'
                        ? 'bg-white text-primary-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="General keyword view"
                  >
                    General
                  </button>
                  <button
                    onClick={() => setKeywordViewMode('coverage')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      keywordViewMode === 'coverage'
                        ? 'bg-white text-primary-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="Category coverage view"
                  >
                    Category Coverage
                  </button>
                </div>

                <div className="h-6 w-px bg-gray-300"></div>
              </>
            )}

            <button
              onClick={() => setShowVolume(!showVolume)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                showVolume
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={showVolume ? 'Hide volume data' : 'Show volume data'}
            >
              {showVolume ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.132m0 0L21 21" />
                </svg>
              )}
              <span>Volume</span>
            </button>

            <button
              onClick={() => setShowIntentScore(!showIntentScore)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                showIntentScore
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={showIntentScore ? 'Hide intent score' : 'Show intent score'}
            >
              {showIntentScore ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span>Intent Score</span>
            </button>

            <div className="h-6 w-px bg-gray-300"></div>

            <button
              onClick={() => setShowSelectedOnly(!showSelectedOnly)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                showSelectedOnly
                  ? 'bg-accent-500 text-white shadow-sm'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title={showSelectedOnly ? 'Show all keywords' : 'Show only selected keywords'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span>{showSelectedOnly ? 'Selected Only' : 'All Keywords'}</span>
            </button>

            {/* Filters Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                  (selectedIntents.length > 0 || selectedRetailers.length > 0 || excludedTerms.length > 0)
                    ? 'bg-accent-500 text-white shadow-sm'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                title="Filter and exclude keywords"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Filters</span>
                {(selectedIntents.length > 0 || selectedRetailers.length > 0 || excludedTerms.length > 0) && (
                  <span className="ml-1 px-1.5 py-0.5 bg-white text-accent-600 rounded text-xs font-bold">
                    {selectedIntents.length + selectedRetailers.length + excludedTerms.length}
                  </span>
                )}
                <svg 
                  className={`w-3 h-3 transition-transform ${showFiltersDropdown ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Filters Dropdown Panel */}
              {showFiltersDropdown && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowFiltersDropdown(false)}
                  />
                  
                  {/* Dropdown Content */}
                  <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-20 max-h-[600px] overflow-y-auto">
                    <div className="p-4">
                      {/* Intent Category Filter */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-semibold text-primary-900">
                            Filter by Intent Category
                          </label>
                          <div className="flex items-center space-x-2">
                            {selectedIntents.length > 0 && (
                              <button
                                onClick={handleClearAllIntents}
                                className="text-xs text-red-600 hover:text-red-700 font-medium"
                              >
                                Clear All
                              </button>
                            )}
                            {project && selectedIntents.length < getIntentCategories(project).length && (
                              <button
                                onClick={handleSelectAllIntents}
                                className="text-xs text-accent-600 hover:text-accent-700 font-medium"
                              >
                                Select All
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {project && getIntentCategories(project).map(intent => {
                            const label = getIntentLabelForProject(project, intent);
                            const color = getIntentColorForProject(project, intent);
                            const isSelected = selectedIntents.includes(intent);
                            
                            // Extract background color for the circle
                            let circleBgColor = 'bg-gray-600';
                            const bgMatch = color.match(/bg-(\w+)-(\d+)/);
                            if (bgMatch) {
                              circleBgColor = `bg-${bgMatch[1]}-${bgMatch[2]}`;
                            }
                            
                            return (
                              <label
                                key={intent}
                                className="flex items-center space-x-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50"
                                style={{
                                  borderColor: isSelected ? '#0ea5e9' : '#e5e7eb',
                                  backgroundColor: isSelected ? '#eff6ff' : 'transparent'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleIntentToggle(intent)}
                                  className="rounded border-gray-300 text-accent-500 focus:ring-accent-500"
                                />
                                <div className={`w-4 h-4 rounded-full ${circleBgColor} border border-gray-300 flex-shrink-0`}></div>
                                <span className="text-sm text-primary-900 font-medium">{label}</span>
                              </label>
                            );
                          })}
                        </div>
                        {selectedIntents.length > 0 && (
                          <p className="text-xs text-gray-500 mt-2">
                            Showing {selectedIntents.length} of {project ? getIntentCategories(project).length : 0} intent {project && getIntentCategories(project).length === 1 ? 'category' : 'categories'}
                          </p>
                        )}
                      </div>

                      {/* Retailer Filter */}
                      {availableRetailers.length > 0 && (
                        <div className="pt-4 border-t border-gray-200">
                          <label className="block text-sm font-semibold text-primary-900 mb-3">
                            Filter by Retailer Availability
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            {availableRetailers.map(retailer => {
                              const isSelected = selectedRetailers.includes(retailer);
                              return (
                                <label
                                  key={retailer}
                                  className="flex items-center space-x-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors hover:bg-gray-50"
                                  style={{
                                    borderColor: isSelected ? '#0ea5e9' : '#e5e7eb',
                                    backgroundColor: isSelected ? '#eff6ff' : 'transparent'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      if (isSelected) {
                                        setSelectedRetailers(prev => prev.filter(r => r !== retailer));
                                      } else {
                                        setSelectedRetailers(prev => [...prev, retailer]);
                                      }
                                    }}
                                    className="rounded border-gray-300 text-accent-500 focus:ring-accent-500"
                                  />
                                  <span className="text-sm text-primary-900 font-medium">{retailer}</span>
                                </label>
                              );
                            })}
                          </div>
                          {selectedRetailers.length > 0 && (
                            <p className="text-xs text-gray-500 mt-2">
                              Showing {selectedRetailers.length} of {availableRetailers.length} retailer{availableRetailers.length !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Exclude Keywords Section */}
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-semibold text-primary-900">
                            Exclude Keywords
                          </label>
                          {excludedTerms.length > 0 && (
                            <button
                              onClick={handleClearAllExcludedTerms}
                              className="text-xs text-red-600 hover:text-red-700 font-medium"
                            >
                              Clear All
                            </button>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mb-2">
                          <input
                            type="text"
                            placeholder="Enter word or phrase..."
                            value={excludeTermInput}
                            onChange={(e) => setExcludeTermInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddExcludedTerm()}
                            className="flex-1 text-sm px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                          />
                          <button
                            onClick={handleAddExcludedTerm}
                            className="px-3 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                            disabled={!excludeTermInput.trim()}
                          >
                            Add
                          </button>
                        </div>
                        {excludedTerms.length > 0 && (
                          <>
                            <div className="flex flex-wrap gap-2">
                              {excludedTerms.map(term => {
                                const count = getExcludedKeywordCountForTerm(term);
                                return (
                                  <span
                                    key={term}
                                    className="inline-flex items-center px-2.5 py-1 bg-red-100 text-red-800 border border-red-200 rounded-lg text-xs font-medium"
                                  >
                                    {term} ({count})
                                    <button
                                      onClick={() => handleRemoveExcludedTerm(term)}
                                      className="ml-1.5 text-red-600 hover:text-red-800 focus:outline-none"
                                      aria-label={`Remove ${term}`}
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Keywords containing these terms will be hidden
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Portfolio Duplication Alert */}
            {portfolioDuplication && portfolioDuplication.totalKeywords >= 2 && (
              <button
                onClick={() => {
                  setDuplicationModalType('portfolio');
                  setShowDuplicationModal(true);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                  (portfolioDuplication.wordAlerts.length > 0 || portfolioDuplication.phraseAlerts.length > 0)
                    ? 'bg-yellow-50 border-2 border-yellow-400 hover:bg-yellow-100 text-yellow-900'
                    : 'bg-gray-50 border border-gray-300 hover:bg-gray-100 text-gray-700'
                }`}
                title="View duplication alerts"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="font-semibold">
                  {portfolioDuplication.wordAlerts.length + portfolioDuplication.phraseAlerts.length}
                </span>
              </button>
            )}

            {/* Advanced Settings Toggle */}
            <button
              onClick={() => {
                const advancedSection = document.getElementById('advanced-settings');
                if (advancedSection) {
                  advancedSection.classList.toggle('hidden');
                }
              }}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center space-x-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200"
              title="Toggle advanced settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Advanced Settings (Collapsible) */}
        <div id="advanced-settings" className="hidden mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Duplication Threshold:</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={Math.round(duplicationThreshold * 100)}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 1 && value <= 100) {
                      setDuplicationThreshold(value / 100);
                    }
                  }}
                  className="w-16 px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                  title="Set duplication threshold percentage"
                />
                <span className="text-sm text-gray-600">%</span>
              </div>
              <p className="text-xs text-gray-500">Keywords with this level of word overlap will be flagged as potential duplicates.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Intent Coverage Summary - Only in Coverage Mode */}
      {keywordViewMode === 'coverage' && (() => {
        const portfolioCoverage = getPortfolioIntentCoverage();
        if (portfolioCoverage.length === 0) return null;

        const totalIntents = portfolioCoverage.length;
        const fullyFulfilledIntents = portfolioCoverage.filter(i => i.isFulfilled).length;

        return (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-primary-900">Portfolio Intent Coverage</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Overview of intent category fulfillment across all products
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary-900">
                    {fullyFulfilledIntents}/{totalIntents}
                  </div>
                  <div className="text-xs text-gray-600">fully covered</div>
                </div>
                <button
                  onClick={() => setPortfolioCoverageExpanded(!portfolioCoverageExpanded)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title={portfolioCoverageExpanded ? 'Collapse' : 'Expand'}
                >
                  <svg 
                    className={`w-5 h-5 text-gray-600 transition-transform ${portfolioCoverageExpanded ? '' : 'rotate-180'}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {portfolioCoverageExpanded && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {portfolioCoverage.map((coverage) => {
                // Extract background color for the circle indicator
                let circleBgColor = 'bg-gray-400';
                const bgMatch = coverage.color.match(/bg-(\w+)-(\d+)/);
                if (bgMatch) {
                  circleBgColor = `bg-${bgMatch[1]}-600`;
                }

                return (
                  <div
                    key={coverage.intent}
                    className="relative p-3 rounded-lg border-2 bg-white transition-all hover:shadow-md"
                    style={{
                      borderColor: coverage.isFulfilled ? '#22c55e' : '#e5e7eb'
                    }}
                  >
                    {/* Progress Bar */}
                    <div className="mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full ${circleBgColor}`}></div>
                          <span className="text-xs font-semibold text-gray-900 truncate">
                            {coverage.label}
                          </span>
                        </div>
                        {coverage.isFulfilled && (
                          <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{
                            width: `${coverage.percentage}%`,
                            backgroundColor: coverage.percentage === 100 ? '#22c55e' : coverage.percentage >= 50 ? '#fbbf24' : '#ef4444'
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-gray-600">
                        {coverage.productsFulfilledCount}/{coverage.productsWithIntentCount} products
                      </span>
                      <span className={`font-semibold ${
                        coverage.percentage === 100 ? 'text-green-600' : 
                        coverage.percentage >= 50 ? 'text-yellow-600' : 
                        'text-red-600'
                      }`}>
                        {Math.round(coverage.percentage)}%
                      </span>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        );
      })()}

      {/* All Products - Grid or Table View */}
      {viewMode === 'table' ? (
        <div className="card">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-primary-900">
                Selected Keywords Table ({getTableData().length} rows)
              </h3>
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={showIngredientsColumn}
                    onChange={(e) => setShowIngredientsColumn(e.target.checked)}
                    className="rounded border-gray-300 text-accent-500 focus:ring-accent-500"
                  />
                  <span>Ingredients</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={showLegalClaimsColumn}
                    onChange={(e) => setShowLegalClaimsColumn(e.target.checked)}
                    className="rounded border-gray-300 text-accent-500 focus:ring-accent-500"
                  />
                  <span>Legal Claims</span>
                </label>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              All selected keywords across all products in a table format
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th
                    onClick={() => handleTableSort('product')}
                    className="text-left py-3 px-4 font-semibold text-primary-900 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span>Product</span>
                      {tableSortColumn === 'product' && (
                        <svg
                          className={`w-4 h-4 ${tableSortDirection === 'asc' ? '' : 'transform rotate-180'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleTableSort('keyword')}
                    className="text-left py-3 px-4 font-semibold text-primary-900 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span>Keyword</span>
                      {tableSortColumn === 'keyword' && (
                        <svg
                          className={`w-4 h-4 ${tableSortDirection === 'asc' ? '' : 'transform rotate-180'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleTableSort('intent')}
                    className="text-left py-3 px-4 font-semibold text-primary-900 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span>Intent</span>
                      {tableSortColumn === 'intent' && (
                        <svg
                          className={`w-4 h-4 ${tableSortDirection === 'asc' ? '' : 'transform rotate-180'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleTableSort('volume')}
                    className="text-left py-3 px-4 font-semibold text-primary-900 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span>Volume</span>
                      {tableSortColumn === 'volume' && (
                        <svg
                          className={`w-4 h-4 ${tableSortDirection === 'asc' ? '' : 'transform rotate-180'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  {showIngredientsColumn && (
                    <th className="text-left py-3 px-4 font-semibold text-primary-900">Ingredients</th>
                  )}
                  {showLegalClaimsColumn && (
                    <th className="text-left py-3 px-4 font-semibold text-primary-900">Legal Claims</th>
                  )}
                  <th className="text-left py-3 px-4 font-semibold text-primary-900 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const sortedData = getSortedTableData();
                  if (sortedData.length === 0) return null;
                  
                  // Determine product order for consistent color assignment
                  const productOrder: string[] = [];
                  sortedData.forEach(r => {
                    if (!productOrder.includes(r.productId)) {
                      productOrder.push(r.productId);
                    }
                  });
                  
                  return sortedData.map((row, index) => {
                    // Alternate between white and gray-50 based on product
                    const productIndex = productOrder.indexOf(row.productId);
                    const rowBgColor = productIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                    
                    return (
                      <tr
                        key={`${row.productId}-${row.keyword}-${index}`}
                        className={`border-b border-gray-200 hover:bg-gray-100 transition-colors ${rowBgColor}`}
                      >
                        <td className="py-3 px-4 text-sm text-primary-900 font-medium">
                          {row.productName}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-primary-900">{row.keyword}</span>
                            {row.isCustom && (
                              <span className="text-xs bg-cyan-100 text-cyan-800 px-2 py-0.5 rounded border border-cyan-200">
                                Custom
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1.5">
                            {row.intents.map((intent) => {
                              const intentColor = getIntentColorForProject(project, intent);
                              const intentLabel = getIntentLabelForProject(project, intent);
                              
                              // Extract background color for the circle indicator
                              let circleBgColor = 'bg-gray-600';
                              const bgMatch = intentColor.match(/bg-(\w+)-(\d+)/);
                              if (bgMatch) {
                                circleBgColor = `bg-${bgMatch[1]}-${bgMatch[2]}`;
                              }
                              
                              return (
                                <div key={intent} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${intentColor}`}>
                                  <div className={`w-2 h-2 rounded-full ${circleBgColor} border border-gray-300`}></div>
                                  <span className="font-medium">{intentLabel}</span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-primary-900">
                          {row.volume > 0 ? row.volume.toLocaleString() : 'N/A'}
                        </td>
                        {showIngredientsColumn && (
                          <td className="py-3 px-4 text-sm text-gray-700 max-w-xs">
                            <div className="truncate" title={row.ingredients || ''}>
                              {row.ingredients || '-'}
                            </div>
                          </td>
                        )}
                        {showLegalClaimsColumn && (
                          <td className="py-3 px-4 text-sm text-gray-700 max-w-xs">
                            <div className="truncate" title={row.legalClaims || ''}>
                              {row.legalClaims || '-'}
                            </div>
                          </td>
                        )}
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleNavigateToProduct(row.productId)}
                              className="text-accent-600 hover:text-accent-800 transition-colors"
                              title="Jump to product in grid view"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                            </button>
                            {row.isCustom && (
                              <button
                                onClick={() => handleDeleteCustomKeyword(row.productId, row.keyword)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Delete custom keyword"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })() || (
                  <tr>
                    <td colSpan={5 + (showIngredientsColumn ? 1 : 0) + (showLegalClaimsColumn ? 1 : 0)} className="py-8 text-center text-gray-500">
                      No keywords selected. Select keywords using the Grid View or Auto Select feature.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
      <div className="space-y-8">
        {getDisplayProducts().map((product, index) => {
          const productKeywords = selectedKeywords[product.id] || [];
          const filteredKeywords = getFilteredKeywords(product);
          const excludedKeywordsList = showExcludedKeywords && excludedTerms.length > 0 
            ? getExcludedKeywords(product) 
            : [];
          
          const isDropTarget = dropTargetProductId === product.id;
          const isDragSource = draggedKeyword?.productId === product.id;
          
          return (
            <div 
              key={product.id} 
              id={`product-${product.id}`} 
              className={`card transition-all duration-300 ${isDropTarget ? 'ring-4 ring-accent-400 bg-accent-50' : ''} ${isDragSource ? 'opacity-60' : ''}`}
              onDragOver={(e) => handleProductDragOver(e, product.id)}
              onDragLeave={handleProductDragLeave}
              onDrop={(e) => handleProductDrop(e, product.id)}
            >
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div>
                      <h3 className="text-xl font-semibold text-primary-900">{product.name}</h3>
                      <p className="text-gray-600 text-sm">{product.description}</p>
                    </div>
                    {/* Product-level Duplication Alert Icon */}
                    {productKeywords.length >= 2 && (() => {
                      const productDuplication = productDuplications.get(product.id);
                      if (productDuplication && (productDuplication.wordAlerts.length > 0 || productDuplication.phraseAlerts.length > 0)) {
                        return (
                          <button
                            onClick={() => {
                              setDuplicationModalType(product.id);
                              setShowDuplicationModal(true);
                            }}
                            className="flex items-center space-x-1 px-2 py-1 bg-orange-50 border border-orange-300 rounded hover:bg-orange-100 transition-colors"
                            title="View duplication alerts"
                          >
                            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <span className="text-xs font-medium text-orange-900">
                              {productDuplication.wordAlerts.length + productDuplication.phraseAlerts.length}
                            </span>
                          </button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <div className="flex items-center space-x-3">
                    {/* Notes Button */}
                    <button
                      onClick={() => handleOpenNotesModal(product)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors flex items-center space-x-1 ${
                        getGroupNote(product)
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                      }`}
                      title={getGroupNote(product) ? 'Edit group notes' : 'Add group notes'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Notes</span>
                      {getGroupNote(product) && (
                        <span className="ml-1 w-2 h-2 bg-white rounded-full"></span>
                      )}
                    </button>
                    {(product.ingredients || product.legalClaims) && (
                      <button
                        onClick={() => {
                          setSelectedProductForInfo(product.id);
                          setShowInfoPanel(true);
                        }}
                        className="px-3 py-1 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center space-x-1"
                        title="View product details"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Info</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleAutoSelect(product.id)}
                      className="px-3 py-1 bg-accent-500 text-white rounded text-sm font-medium hover:bg-accent-600 transition-colors flex items-center space-x-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Auto Keyword</span>
                    </button>
                    <button
                      onClick={() => handleAutoSelectIntentProduct(product.id)}
                      className="px-3 py-1 bg-accent-500 text-white rounded text-sm font-medium hover:bg-accent-600 transition-colors flex items-center space-x-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      <span>Auto Intent</span>
                    </button>
                    <button
                      onClick={() => handleDeselectProduct(product.id)}
                      className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Clear All</span>
                    </button>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">
                        {filteredKeywords.filter(k => productKeywords.includes(k.text)).length} of {filteredKeywords.length} selected
                      </div>
                      <div className="text-xs text-gray-500">{product.category}</div>
                    </div>
                  </div>
                </div>

                {/* Category Coverage View */}
                {keywordViewMode === 'coverage' && (() => {
                  const intentCoverage = getIntentCoverage(product);
                  if (intentCoverage.length === 0) return null;

                  const fulfilledCount = intentCoverage.filter(i => i.isFulfilled).length;
                  const totalCount = intentCoverage.length;
                  const productCustomKeywords = customKeywords[product.id] || [];
                  const allKeywords = [...product.keywords, ...productCustomKeywords];

                  return (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900">
                          Intent Category Coverage
                        </h4>
                        <span className="text-xs font-medium text-gray-600">
                          {fulfilledCount} of {totalCount} fulfilled
                        </span>
                      </div>
                      <div className="space-y-2">
                        {intentCoverage.map((coverage) => {
                          // Get keywords for this intent
                          const intentKeywords = allKeywords.filter(k => k.intent === coverage.intent);
                          const productSelectedKeywords = selectedKeywords[product.id] || [];

                          return (
                            <div
                              key={coverage.intent}
                              className={`relative flex gap-3 p-3 rounded-lg border-2 transition-all ${
                                coverage.isFulfilled
                                  ? 'border-green-400 bg-green-50'
                                  : 'border-gray-200 bg-white'
                              }`}
                            >
                              {/* Left Side: Intent Label */}
                              <div className="flex-shrink-0 w-48 flex items-start gap-2">
                                {/* Checkmark or Empty Circle - Clickable to toggle intent selection */}
                                <button
                                  onClick={() => {
                                    setExplicitlySelectedIntents(prev => {
                                      const currentIntents = prev[product.id] || [];
                                      const isCurrentlySelected = currentIntents.includes(coverage.intent);
                                      
                                      if (isCurrentlySelected) {
                                        // Remove from explicitly selected
                                        return {
                                          ...prev,
                                          [product.id]: currentIntents.filter(i => i !== coverage.intent)
                                        };
                                      } else {
                                        // Add to explicitly selected
                                        return {
                                          ...prev,
                                          [product.id]: [...currentIntents, coverage.intent]
                                        };
                                      }
                                    });
                                  }}
                                  className="flex-shrink-0 mt-0.5 cursor-pointer hover:opacity-80 transition-opacity"
                                  title={coverage.isFulfilled ? "Click to deselect this intent" : "Click to select this intent"}
                                >
                                  {coverage.isFulfilled ? (
                                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    </div>
                                  ) : (
                                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-white hover:border-gray-400"></div>
                                  )}
                                </button>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center mb-2">
                                    <span className={`text-sm font-semibold ${
                                      coverage.isFulfilled ? 'text-gray-900' : 'text-gray-500'
                                    }`}>
                                      {coverage.label}
                                    </span>
                                  </div>
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => {
                                        // Select all keywords for this intent
                                        const keywordsToSelect = intentKeywords.map(k => k.text);
                                        setSelectedKeywords(prev => {
                                          const currentSelected = prev[product.id] || [];
                                          // Add all keywords from this intent that aren't already selected
                                          const uniqueSet = new Set([...currentSelected, ...keywordsToSelect]);
                                          const newSelected = Array.from(uniqueSet);
                                          return {
                                            ...prev,
                                            [product.id]: newSelected
                                          };
                                        });
                                        // Mark this intent as explicitly selected
                                        setExplicitlySelectedIntents(prev => {
                                          const currentIntents = prev[product.id] || [];
                                          if (!currentIntents.includes(coverage.intent)) {
                                            return {
                                              ...prev,
                                              [product.id]: [...currentIntents, coverage.intent]
                                            };
                                          }
                                          return prev;
                                        });
                                      }}
                                      className="px-2 py-0.5 text-[10px] font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
                                    >
                                      Select All
                                    </button>
                                    {coverage.selectedCount > 0 && (
                                      <button
                                        onClick={() => {
                                          // Deselect all keywords for this intent
                                          const keywordsToDeselect = intentKeywords.map(k => k.text);
                                          setSelectedKeywords(prev => {
                                            const currentSelected = prev[product.id] || [];
                                            // Remove all keywords from this intent
                                            const newSelected = currentSelected.filter(k => !keywordsToDeselect.includes(k));
                                            return {
                                              ...prev,
                                              [product.id]: newSelected
                                            };
                                          });
                                          // Remove this intent from explicitly selected
                                          setExplicitlySelectedIntents(prev => {
                                            const currentIntents = prev[product.id] || [];
                                            return {
                                              ...prev,
                                              [product.id]: currentIntents.filter(i => i !== coverage.intent)
                                            };
                                          });
                                        }}
                                        className="px-2 py-0.5 text-[10px] font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                                      >
                                        Clear
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Right Side: Keywords */}
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap gap-1.5">
                                  {intentKeywords.map(keyword => {
                                    const isSelected = productSelectedKeywords.includes(keyword.text);
                                    const isCustom = isCustomKeyword(product.id, keyword.text);
                                    
                                    return (
                                      <KeywordPill
                                        key={keyword.text}
                                        keyword={keyword}
                                        productId={product.id}
                                        isSelected={isSelected}
                                        isCustom={isCustom}
                                        showVolume={showVolume}
                                        showIntentScore={showIntentScore}
                                        onToggle={handleKeywordToggle}
                                        onDelete={handleDeleteCustomKeyword}
                                        onRemoveCopy={handleRemoveCopiedKeyword}
                                        onDragStart={handleKeywordDragStart}
                                        onDragEnd={handleKeywordDragEnd}
                                        onContextMenu={handleKeywordContextMenu}
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* General View */}
                {keywordViewMode === 'general' && (
                  <>
                    {/* Selected Keywords Summary */}
                {productKeywords.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Selected Keywords ({productKeywords.length}):
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {productKeywords.map(keywordText => {
                        const keyword = product.keywords.find(k => k.text === keywordText) ||
                                       customKeywords[product.id]?.find(k => k.text === keywordText);
                        const color = keyword ? getIntentColorForProject(project, keyword.intent) : 'bg-accent-100 text-accent-800 border-accent-200';
                        const isCustom = isCustomKeyword(product.id, keywordText);
                        return (
                          <span 
                            key={keywordText} 
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border ${color} ${isCustom ? 'group' : ''}`}
                            title={showVolume && keyword && keyword.volume ? `Volume: ${keyword.volume.toLocaleString()}` : ''}
                          >
                            <span>{keywordText} {showVolume && keyword && keyword.volume && `(${keyword.volume.toLocaleString()})`}</span>
                            {isCustom && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCustomKeyword(product.id, keywordText);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-800"
                                title="Delete custom keyword"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                    {/* Keywords Grid */}
                    <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-4">
                      <div className="flex flex-wrap gap-2">
                        {filteredKeywords.length > 0 ? (
                          <>
                            {filteredKeywords.map((keyword, keywordIndex) => {
                              const intentColor = getIntentColorForProject(project, keyword.intent);
                              const intentLabel = getIntentLabelForProject(project, keyword.intent);
                              const isSelected = productKeywords.includes(keyword.text);
                              const isCustom = isCustomKeyword(product.id, keyword.text);
                              return (
                                <KeywordPill
                                  key={`${product.id}-${keywordIndex}-${keyword.text}`}
                                  keyword={keyword}
                                  productId={product.id}
                                  isSelected={isSelected}
                                  isCustom={isCustom}
                                  showVolume={showVolume}
                                  showIntentScore={showIntentScore}
                                  intentColor={intentColor}
                                  intentLabel={intentLabel}
                                  onToggle={handleKeywordToggle}
                                  onDelete={handleDeleteCustomKeyword}
                                  onRemoveCopy={handleRemoveCopiedKeyword}
                                  onDragStart={handleKeywordDragStart}
                                  onDragEnd={handleKeywordDragEnd}
                                  onContextMenu={handleKeywordContextMenu}
                                />
                              );
                            })}
                            {/* Add Custom Keyword Button */}
                            {expandedCustomKeywordProductId === product.id ? (
                              <div className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-accent-400 rounded-lg bg-accent-50">
                                <input
                                  type="text"
                                  placeholder="Enter keyword..."
                                  value={customKeyword}
                                  onChange={(e) => setCustomKeyword(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleAddCustomKeyword(product.id);
                                    } else if (e.key === 'Escape') {
                                      setExpandedCustomKeywordProductId(null);
                                      setCustomKeyword('');
                                      setCustomKeywordVolume('0');
                                    }
                                  }}
                                  autoFocus
                                  className="text-sm px-2 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 min-w-[120px]"
                                />
                                <input
                                  type="number"
                                  placeholder="Volume"
                                  value={customKeywordVolume}
                                  onChange={(e) => setCustomKeywordVolume(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleAddCustomKeyword(product.id);
                                    } else if (e.key === 'Escape') {
                                      setExpandedCustomKeywordProductId(null);
                                      setCustomKeyword('');
                                      setCustomKeywordVolume('0');
                                    }
                                  }}
                                  min="0"
                                  className="text-sm px-2 py-1 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 w-24"
                                />
                                <button
                                  onClick={() => handleAddCustomKeyword(product.id)}
                                  className="px-2 py-1 bg-accent-500 text-white rounded text-xs font-medium hover:bg-accent-600 transition-colors"
                                  disabled={!customKeyword.trim()}
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => handleToggleCustomKeywordInput(product.id)}
                                  className="px-2 py-1 text-gray-600 hover:text-gray-800 transition-colors"
                                  title="Cancel"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleToggleCustomKeywordInput(product.id)}
                                className="px-3 py-2 rounded-lg text-sm font-medium transition-colors border-2 border-dashed border-gray-300 bg-gray-50 text-gray-600 hover:border-accent-400 hover:bg-accent-50 hover:text-accent-600 flex items-center gap-1"
                                title="Add custom keyword"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                <span>Add Custom Keyword</span>
                              </button>
                            )}
                          </>
                        ) : (
                          <div className="text-center text-gray-500 py-8 w-full">
                            {showSelectedOnly 
                              ? 'No keywords selected for this product'
                              : 'No keywords available'
                            }
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Excluded Keywords Section */}
                    {showExcludedKeywords && excludedTerms.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <span>Excluded Keywords {excludedKeywordsList.length > 0 && `(${excludedKeywordsList.length})`}:</span>
                          <span className="text-xs text-gray-500 font-normal">Hidden from selection</span>
                        </h4>
                        {excludedKeywordsList.length > 0 ? (
                          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex flex-wrap gap-2">
                              {excludedKeywordsList.map(keyword => {
                                const intentColor = getIntentColorForProject(project, keyword.intent);
                                const intentLabel = getIntentLabelForProject(project, keyword.intent);
                                return (
                                  <div
                                    key={keyword.text}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium border opacity-60 cursor-not-allowed ${intentColor}`}
                                    title={`${intentLabel}: ${keyword.text}${showVolume && keyword.volume ? ` (Volume: ${keyword.volume.toLocaleString()})` : ''} - Excluded`}
                                  >
                                    <div className="flex flex-col items-center">
                                      <span>{keyword.text}</span>
                                      {showVolume && (
                                        <span className="text-xs opacity-75 font-normal">
                                          {keyword.volume ? keyword.volume.toLocaleString() : 'N/A'}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <p className="text-sm text-gray-500 text-center">
                              No keywords found containing the excluded terms for this product.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

            </div>
          );
        })}
      </div>
      )}

      {/* Save Actions */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Total selected keywords: {Object.values(selectedKeywords).reduce((sum, keywords) => sum + keywords.length, 0)}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => project && exportSelectionsToExcel(project, selectedKeywords)}
              className="btn-primary flex items-center space-x-2"
              disabled={Object.values(selectedKeywords).reduce((sum, keywords) => sum + keywords.length, 0) === 0}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download Selections to Excel</span>
            </button>
            <button
              onClick={() => project && exportGroupNotesToExcel(project)}
              className="btn-primary bg-blue-600 hover:bg-blue-700 flex items-center space-x-2"
              disabled={!project?.groupNotes || Object.keys(project.groupNotes).length === 0}
              title="Export all group notes for feedback"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Export Group Notes</span>
            </button>
            <button
              onClick={handleSaveProject}
              className="btn-primary bg-green-600 hover:bg-green-700 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Save Project</span>
            </button>
          </div>
        </div>
      </div>

      {/* Auto Select Modal */}
      {project && (
        <AutoSelectModal
          isOpen={showAutoSelectModal}
          onClose={handleAutoSelectModalClose}
          onConfirm={handleAutoSelectModalConfirm}
          project={project}
          preselectedProductId={preselectedProductId}
        />
      )}

      {/* Auto Select Intent Modal */}
      {project && (
        <AutoSelectIntentModal
          isOpen={showAutoSelectIntentModal}
          onClose={handleAutoSelectIntentModalClose}
          onConfirm={handleAutoSelectIntentModalConfirm}
          project={project}
          preselectedProductId={preselectedProductIdIntent}
        />
      )}

      {/* Duplication Details Modal */}
      {showDuplicationModal && project && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-primary-900">
                {duplicationModalType === 'portfolio' 
                  ? 'Portfolio Duplication Alerts' 
                  : (() => {
                      const product = project.products.find(p => p.id === duplicationModalType);
                      return product ? `Duplication Alerts - ${product.name}` : 'Duplication Alerts';
                    })()}
              </h2>
              <button
                onClick={() => {
                  setShowDuplicationModal(false);
                  setDuplicationModalType(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            {(() => {
              let analysis: ProductDuplicationAnalysis | PortfolioDuplicationAnalysis | null = null;
              
              if (duplicationModalType === 'portfolio') {
                analysis = portfolioDuplication;
              } else if (duplicationModalType) {
                analysis = productDuplications.get(duplicationModalType) || null;
              }

              if (!analysis) {
                return (
                  <p className="text-gray-600">No duplication data available.</p>
                );
              }

              const hasAlerts = analysis.wordAlerts.length > 0 || analysis.phraseAlerts.length > 0;

              if (!hasAlerts) {
                return (
                  <p className="text-sm text-gray-600">
                    ✓ No duplication issues detected. Words appear less than 40% of the time.
                  </p>
                );
              }

              // TypeScript now knows analysis is not null after the checks above
              const analysisData = analysis;

              return (
                <div className="space-y-6">
                  {/* Word Duplication Alerts */}
                  {analysisData.wordAlerts.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Word Duplication (&gt;{Math.round(duplicationThreshold * 100)}%):
                      </h3>
                      <div className="space-y-3">
                        {analysisData.wordAlerts.map((alert, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-900">
                                "{alert.word}" appears in {alert.frequency} of {analysisData.totalKeywords} keyword{alert.frequency !== 1 ? 's' : ''}
                              </span>
                              <span className="text-sm font-semibold text-red-600">
                                {(alert.percentage * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-2">
                              <span className="font-medium">Keywords containing this word:</span>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {alert.keywords.map((kw, i) => (
                                  <span key={i} className="px-2 py-1 bg-white border border-gray-200 rounded text-sm">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Phrase Duplication Alerts */}
                  {analysisData.phraseAlerts.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Duplicate Phrases:
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {analysisData.phraseAlerts.map((phrase, idx) => (
                          <span key={idx} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm">
                            "{phrase}"
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowDuplicationModal(false);
                  setDuplicationModalType(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Product Info Sidebar Panel */}
      <div className={`fixed top-0 right-0 h-screen w-96 bg-white shadow-2xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out z-40 ${
        showInfoPanel ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {selectedProductForInfo && (() => {
          const selectedProduct = project.products.find(p => p.id === selectedProductForInfo);
          if (!selectedProduct) return null;

          return (
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="bg-primary-900 text-white px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold">Product Details</h2>
                <button
                  onClick={() => {
                    setShowInfoPanel(false);
                    setSelectedProductForInfo(null);
                  }}
                  className="text-white hover:text-gray-300 transition-colors"
                  title="Close panel"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Product Name and Category */}
                <div>
                  <h3 className="text-lg font-semibold text-primary-900 mb-1">{selectedProduct.name}</h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span>{selectedProduct.category}</span>
                  </div>
                  {selectedProduct.description && (
                    <p className="text-sm text-gray-600 mt-2">{selectedProduct.description}</p>
                  )}
                </div>

                {/* Product Statistics */}
                <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Statistics</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-gray-600 mb-1">Total</p>
                      <p className="text-2xl font-bold text-primary-900">{selectedProduct.keywords.length}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <p className="text-xs text-gray-600 mb-1">Selected</p>
                      <p className="text-2xl font-bold text-accent-600">{selectedKeywords[selectedProduct.id]?.length || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Ingredients Section */}
                {selectedProduct.ingredients && (
                  <div>
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      </div>
                      <h4 className="text-base font-semibold text-gray-900">Ingredients</h4>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-4 border-l-4 border-indigo-400">
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedProduct.ingredients}</p>
                    </div>
                  </div>
                )}

                {/* Legal Claims Section */}
                {selectedProduct.legalClaims && (
                  <div>
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <h4 className="text-base font-semibold text-gray-900">Legal Claims</h4>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-400">
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedProduct.legalClaims}</p>
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {!selectedProduct.ingredients && !selectedProduct.legalClaims && (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-gray-500">No additional product information available</p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Deduplication Modal */}
      {showDeduplicationModal && (
        <DeduplicationModal
          project={project}
          onClose={() => setShowDeduplicationModal(false)}
          onApplyDeduplication={handleApplyDeduplication}
          deduplicationState={deduplicationState}
          onRestoreDeduplication={handleRestoreDeduplication}
        />
      )}

      {/* Copy Keyword Panel */}
      {project && (
        <CopyKeywordPanel
          isOpen={showCopyPanel}
          keyword={keywordToCopy?.keyword || null}
          sourceProductId={keywordToCopy?.sourceProductId || null}
          sourceGroupName={keywordToCopy?.sourceName || null}
          allProducts={project.products}
          currentViewType={project.viewType || 'product'}
          onClose={() => {
            setShowCopyPanel(false);
            setKeywordToCopy(null);
          }}
          onCopy={handleCopyKeywordConfirm}
        />
      )}

      {/* Group Notes Modal */}
      {project && selectedGroupForNotes && (
        <GroupNotesModal
          isOpen={showGroupNotesModal}
          onClose={handleCloseNotesModal}
          onSave={handleSaveGroupNote}
          project={project}
          groupKey={selectedGroupForNotes.key}
          groupName={selectedGroupForNotes.name}
          viewType={project.viewType || 'product'}
          currentNote={project.groupNotes?.[selectedGroupForNotes.key] || ''}
        />
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-8 right-8 z-50 animate-fade-in">
          <div className={`px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 ${
            toastMessage.type === 'success' ? 'bg-green-600 text-white' :
            toastMessage.type === 'error' ? 'bg-red-600 text-white' :
            'bg-yellow-600 text-white'
          }`}>
            {toastMessage.type === 'success' && (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {toastMessage.type === 'error' && (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {toastMessage.type === 'warning' && (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            <span className="font-medium">{toastMessage.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectEvaluation;
